/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { RecordingsServiceRepositoryImpl } from '../impls/RecordingsServiceRepositoryImpl';
import { IRecordingsService, IRecordingsServiceRepository } from '../interfaces';
import { TiVoNetworkRequestModel } from '../tivo';
import {
    THistoryValues,
    TRecordingOptions,
    TRecordingValues,
    TRecordingValuesWithDisplayOptions,
    TServicesParams,
    TToDoValues,
} from '../types';

export class RecordingsService implements IRecordingsService {
    private static TAG: string = 'RecordingsService';

    private mRecordingServiceRepository: IRecordingsServiceRepository;

    public constructor(params: TServicesParams) {
        this.mRecordingServiceRepository = new RecordingsServiceRepositoryImpl(params);
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToCancelRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(
            this.mRecordingServiceRepository.cancelRecordingOfferIdSchedule.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(contentId, offerId);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToCreateRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(
            this.mRecordingServiceRepository.createRecordingOfferIdSchedule.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(contentId, offerId, tRecordingValues);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToDeleteRecordingSchedule(
        recordingId: string,
    ): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(
            this.mRecordingServiceRepository.deleteRecordingSchedule.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(recordingId);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToFetchRecordingsLastViewedPosition(
        recordingId: string,
    ): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(
            this.mRecordingServiceRepository.fetchRecordingsLastViewedPosition.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(recordingId);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelForOfferIdRecordingOptions(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<TRecordingOptions> {
        const oRequestModel = new TiVoNetworkRequestModel<TRecordingOptions>(
            this.mRecordingServiceRepository.fetchOfferIdRecordingOptions.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(contentId, offerId);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelForRecordingValuesWithDisplayOptions(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<TRecordingValuesWithDisplayOptions> {
        const oRequestModel = new TiVoNetworkRequestModel<TRecordingValuesWithDisplayOptions>(
            this.mRecordingServiceRepository.fetchRecordingValuesWithDisplayOptions.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(contentId, offerId);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelForRecordingIdRecordingOptions(
        contentId: string,
        recordingId: string,
    ): TiVoNetworkRequestModel<TRecordingOptions> {
        const oRequestModel = new TiVoNetworkRequestModel<TRecordingOptions>(
            this.mRecordingServiceRepository.fetchRecordingIdRecordingOptions.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(contentId, recordingId);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelForRecordingValues(
        contentId: string,
        recordingId: string,
    ): TiVoNetworkRequestModel<TRecordingValues> {
        const oRequestModel = new TiVoNetworkRequestModel<TRecordingValues>(
            this.mRecordingServiceRepository.fetchRecordingValues.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(contentId, recordingId);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToRecordingRecovery(recordingId: string): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(
            this.mRecordingServiceRepository.recordingRecovery.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(recordingId);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToUpdateRecordingsLastViewedPosition(
        recordingId: string,
        lastViewedPosition: number,
    ): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(
            this.mRecordingServiceRepository.updateRecordingsLastViewedPosition.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(recordingId, lastViewedPosition);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToUpdateRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(
            this.mRecordingServiceRepository.updateRecordingOfferIdSchedule.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(contentId, offerId, tRecordingValues);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToUpdateRecordingSchedule(
        contentId: string,
        recordingId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(
            this.mRecordingServiceRepository.updateRecordingSchedule.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(contentId, recordingId, tRecordingValues);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToFetchToDoItems(
        offset: number,
        count: number,
    ): TiVoNetworkRequestModel<TToDoValues> {
        const oRequestModel = new TiVoNetworkRequestModel<TToDoValues>(
            this.mRecordingServiceRepository.fetchToDoItems.bind(this.mRecordingServiceRepository),
        );
        oRequestModel.addRequestParams(offset, count);
        return oRequestModel;
    }

    /**
     * @inheritdoc
     */
    public getRequestModelToFetchRecordingHistory(
        offset: number,
        count: number,
    ): TiVoNetworkRequestModel<THistoryValues> {
        const oRequestModel = new TiVoNetworkRequestModel<THistoryValues>(
            this.mRecordingServiceRepository.fetchRecordingHistory.bind(
                this.mRecordingServiceRepository,
            ),
        );
        oRequestModel.addRequestParams(offset, count);
        return oRequestModel;
    }
}
